import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, KeyRound, Copy, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SeedTrialCard — one-click provisioning for evaluators.
 *
 * Seeds a fresh demo company (Trial 2 · Al Noor Realty) with 2 buildings,
 * 3 tenants, 6 stock items and 4 users (Company Admin / Engineer / Storekeeper
 * / Technician) all sharing the returned password. Idempotent — safe to click
 * again on the same environment; a second click just updates the passwords.
 *
 * Because preview + production have separate databases, the SuperAdmin must
 * click this button ONCE on each environment before the 4 test emails can
 * log in there.
 *
 * @param {object} props
 * @param {'compact'|'full'} [props.variant='full']  compact = warning-banner style;
 *                                                   full   = card with description.
 */
export default function SeedTrialCard({ variant = 'full' }) {
  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/superadmin/seed-trial-accounts`, {}, { withCredentials: true });
      setResult(r.data);
      toast.success('Trial accounts ready — share the credentials below with your evaluator');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed trial accounts');
    } finally {
      setSeeding(false);
    }
  };

  const copyAll = () => {
    if (!result) return;
    const rows = [
      `FM Flexi Pro™ — Trial Login Credentials`,
      `Login URL: ${window.location.origin}/login`,
      `Company:   ${result.company_name}`,
      `Password (same for all 4): ${result.password}`,
      '',
      ...result.users.map(u => `${u.role.padEnd(12)}  →  ${u.email}`),
    ].join('\n');
    navigator.clipboard.writeText(rows);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied — paste into an email / WhatsApp to the evaluator');
  };

  const compact = variant === 'compact';

  return (
    <Card
      data-testid="seed-trial-card"
      className={`${compact ? 'p-4' : 'p-5'} border-amber-300 bg-gradient-to-br from-amber-50 to-white`}
    >
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
            {compact ? <AlertTriangle className="w-5 h-5 text-white" /> : <Sparkles className="w-5 h-5 text-white" />}
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900">
              {compact ? 'Trial accounts not on this environment yet?' : 'One-click Demo Trial Accounts'}
            </h3>
            <p className="text-sm text-slate-600 max-w-xl mt-0.5">
              {compact ? (
                <>
                  Preview and production have separate databases. Click <strong>Seed Trial Accounts</strong> to
                  provision the demo tenant + 4 users (Admin/Engineer/Storekeeper/Technician) on <strong>this</strong> environment.
                  Safe to click multiple times.
                </>
              ) : (
                <>
                  Provisions a self-contained demo tenant (2 buildings · 3 tenants · 6 stock items) plus
                  4 walkthrough users — <strong>Company Admin</strong>, <strong>Engineer / Supervisor</strong>,
                  <strong> Storekeeper</strong>, <strong>Technician</strong> — all sharing one password.
                  Safe to click multiple times (idempotent).
                </>
              )}
            </p>
          </div>
        </div>
        <Button
          data-testid="seed-trial-btn"
          onClick={seed}
          disabled={seeding}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          {seeding ? 'Seeding…' : (result ? 'Re-seed' : 'Seed Trial Accounts')}
        </Button>
      </div>

      {result && (
        <div data-testid="seed-trial-result" className="mt-5 pt-4 border-t border-amber-200 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-slate-700">
              <span className="font-semibold">Company:</span> {result.company_name}
              <span className="mx-2 text-slate-400">·</span>
              <span className="font-semibold">Password (all 4):</span>
              <code className="ml-1 px-1.5 py-0.5 rounded bg-slate-800 text-amber-200 font-mono text-[13px]" data-testid="seed-trial-password">{result.password}</code>
            </div>
            <Button variant="outline" size="sm" onClick={copyAll} data-testid="seed-trial-copy-btn">
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? 'Copied!' : 'Copy all 4'}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {result.users.map(u => (
              <div key={u.email} data-testid={`seed-trial-row-${u.role}`} className="flex items-center gap-2 p-2.5 rounded-md bg-white border border-slate-200 text-sm">
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-700 flex-shrink-0">{u.role}</span>
                <code className="font-mono text-[12.5px] text-slate-800 truncate">{u.email}</code>
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${u.action === 'created' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{u.action}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Send the emails + password to the evaluator. They log in at <code>{window.location.origin}/login</code>.
          </p>
        </div>
      )}
    </Card>
  );
}
